import { ClinicalLibrariesReadDtoReturn } from "functions/api/library/getLibraries";
import { useEffect } from "react";
import {
  IUnifiedAction,
  RECONCILE_CLINICAL_LIBRARIES,
  SET_CLINICAL_LIBRARY_LOADING,
} from "./interface";

/**
 * Existing route-loader request and dispatcher for its app-owned lifecycle.
 */
interface UseAppClinicalLibraryLoaderEffectProps {
  clinicalLibraryPromise?: Promise<ClinicalLibrariesReadDtoReturn>;
  dispatch: React.Dispatch<IUnifiedAction>;
}

/**
 * Applies the deferred Clinical Library result returned by the active route loader.
 *
 * The loader owns request creation and cancellation. The app-level effect updates
 * the unified cache while the associated route visit is active.
 *
 * @param props - Deferred Clinical Library request and unified dispatcher
 * @param props.clinicalLibraryPromise - Promise for the deferred Clinical Library request
 * @param props.dispatch - Dispatcher for unified state updates
 */
export const useAppClinicalLibraryLoaderEffect = ({
  clinicalLibraryPromise,
  dispatch,
}: UseAppClinicalLibraryLoaderEffectProps): void => {
  useEffect(() => {
    let isActive = true;

    if (!clinicalLibraryPromise) {
      dispatch({
        operation: SET_CLINICAL_LIBRARY_LOADING,
        isClinicalLibraryLoading: false,
      });

      return;
    }

    // The loader has started the deferred CL request,
    // so show the loading state until it completes.
    dispatch({
      operation: SET_CLINICAL_LIBRARY_LOADING,
      isClinicalLibraryLoading: true,
    });

    void clinicalLibraryPromise
      .then((response: ClinicalLibrariesReadDtoReturn) => {
        if (isActive) {
          if (response.success) {
            dispatch({
              operation: RECONCILE_CLINICAL_LIBRARIES,
              clinicalLibraries: response.data,
            });
          }

          dispatch({
            operation: SET_CLINICAL_LIBRARY_LOADING,
            isClinicalLibraryLoading: false,
          });
        }
      })
      .catch((error: unknown) => {
        if (isActive) {
          console.warn("Clinical Library loading failed:", error);
          dispatch({
            operation: SET_CLINICAL_LIBRARY_LOADING,
            isClinicalLibraryLoading: false,
          });
        }
      });

    return () => {
      isActive = false;
    };
  }, [clinicalLibraryPromise, dispatch]);
};
